import { PantallaCargando } from '@/components/Cargando';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ExternalLink, Globe, Plus, Trash2 } from 'lucide-react';

/**
 * Sitio Web — S77.
 *
 * El módulo se redujo a lo que funciona: hospedaje real, dominio propio con su
 * DNS, medición y formularios. El constructor visual, el asistente de IA, las
 * plantillas, el blog y el A/B testing se retiraron.
 */
export default function WebDashboard() {
  const [status, setStatus] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([
        api.get('/tenant/web/status'),
        api.get('/tenant/web/sites').catch(() => ({ data: [] })),
      ]);
      setStatus(s.data);
      setSites(list.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Módulo Web no disponible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeSite = async (site) => {
    // Doble confirmación escribiendo el nombre: borrar un sitio publicado tumba
    // lo que hay en el dominio del cliente, y no hay deshacer.
    const typed = window.prompt(
      `Vas a eliminar «${site.name}» con todos sus archivos y versiones.\n` +
      'Esto no se puede deshacer.\n\n' +
      'Escribe el nombre del sitio para confirmar:');
    if (typed === null) return;
    if (typed.trim() !== site.name) {
      toast.error('El nombre no coincide. No se eliminó nada.');
      return;
    }
    setDeleting(site.id);
    try {
      await api.delete(`/tenant/web/sites/${site.id}`);
      toast.success(`«${site.name}» eliminado`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(null);
    }
  };

  if (loading) return <PantallaCargando />;

  const enabled = status?.flags?.WEB_BUILDER_ENABLED && status?.plan_web_builder_enabled;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="tenant-web-dashboard">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <Globe className="w-7 h-7 text-[#F72585]" /> Sitio Web
          </h1>
          <p className="text-sm text-[#64748B] mt-1 max-w-2xl">
            Sube el sitio de tu negocio o edítalo aquí mismo, conéctalo a tu propio
            dominio y mide sus visitas. Tus archivos se publican tal cual: no
            cambiamos tu diseño.
          </p>
        </div>
        {enabled && (
          <Button asChild data-testid="web-create-site">
            <Link to="/tenant/web/sites/new"><Plus className="w-4 h-4 mr-1" /> Crear sitio</Link>
          </Button>
        )}
      </div>

      {!enabled && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          El módulo de sitios web todavía no está activo en tu cuenta. Escríbenos
          y lo habilitamos.
        </div>
      )}

      <section className="grid sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-[#E2E8F0] p-4 bg-white">
          <p className="text-xs uppercase text-[#64748B] font-semibold">Sitios</p>
          <p className="text-2xl font-bold mt-1">{sites.length}</p>
          <p className="text-xs text-[#64748B]">Tu plan incluye {status?.limits?.max_websites ?? '—'}</p>
        </div>
        <div className="rounded-xl border border-[#E2E8F0] p-4 bg-white">
          <p className="text-xs uppercase text-[#64748B] font-semibold">Archivos por sitio</p>
          <p className="text-2xl font-bold mt-1">{status?.limits?.max_site_files ?? '—'}</p>
          <p className="text-xs text-[#64748B]">Las imágenes no cuentan aquí</p>
        </div>
        <div className="rounded-xl border border-[#E2E8F0] p-4 bg-white">
          <p className="text-xs uppercase text-[#64748B] font-semibold">Dominios propios</p>
          <p className="text-2xl font-bold mt-1">{status?.limits?.max_web_custom_domains ?? '—'}</p>
          <p className="text-xs text-[#64748B]">Con su DNS configurado por nosotros</p>
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold text-[#0F172A]">Mis sitios</h2>
        {!sites.length && (
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-8 text-center">
            <p className="text-sm text-[#475569]">
              Todavía no tienes sitios. Crea uno y sube tu .zip, o empieza con la
              página en blanco que te dejamos lista.
            </p>
          </div>
        )}
        {!!sites.length && (
          <ul className="space-y-2">
            {sites.map((s) => (
              <li key={s.id}
                  className="rounded-xl border border-[#E2E8F0] bg-white p-4 flex flex-wrap
                             items-center justify-between gap-3"
                  data-testid={`web-site-row-${s.slug}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-[#0F172A] truncate">{s.name}</p>
                  <p className="text-xs text-[#64748B]">
                    {s.published_release_id
                      ? 'Publicado'
                      : 'Sin publicar — todavía no se ve en tu dominio'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="outline" asChild>
                    <Link to={`/tenant/web/sites/${s.id}`}>
                      <ExternalLink className="w-4 h-4 mr-1.5" /> Abrir
                    </Link>
                  </Button>
                  <Button size="sm" variant="ghost"
                          onClick={() => removeSite(s)}
                          disabled={deleting === s.id}
                          aria-label={`Eliminar ${s.name}`}
                          className="text-[#94A3B8] hover:text-red-600"
                          data-testid={`web-site-delete-${s.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
